import { useQueryClient } from '@tanstack/react-query'
import { api } from '../http'
import { buildJsonApiParams } from '../jsonApi'
import { queryKeys } from '../queryKeys'
import {
    useApiQuery,
    useApiInfiniteQuery,
    useApiMutation,
} from './_base'

/** GET /companies/{companyId}/assets */
export function useAssetList(params = {}, options = {}) {
    const { companyId, ...rest } = params
    const apiParams = buildJsonApiParams(rest)
    return useApiQuery({
        queryKey: queryKeys.assets.list({ companyId, ...apiParams }),
        url: `/companies/${companyId}/assets`,
        params: apiParams,
        options: { enabled: !!companyId, ...options },
    })
}

/** GET /companies/{companyId}/assets (infinite, JSON:API link-based) */
export function useAssetsInfinite(params = {}, options = {}) {
    const { companyId, ...rest } = params
    const apiParams = buildJsonApiParams(rest)
    return useApiInfiniteQuery({
        queryKey: queryKeys.assets.infinite({ companyId, ...apiParams }),
        url: `/companies/${companyId}/assets`,
        params: apiParams,
        options: { enabled: !!companyId, ...options },
    })
}

/** GET /assets/{id} */
export function useAsset(id, params = {}, options = {}) {
    const apiParams = buildJsonApiParams(params)
    return useApiQuery({
        queryKey: queryKeys.assets.detail(id, apiParams),
        url: `/assets/${id}`,
        params: apiParams,
        options: { enabled: !!id, ...options },
    })
}

/** POST /companies/{companyId}/assets — create asset metadata row. */
export function useCreateAsset() {
    const qc = useQueryClient()
    return useApiMutation({
        mutationKey: ['assets', 'create'],
        mutationFn: ({ companyId, ...payload }) =>
            api.post(`/companies/${companyId}/assets`, {}, payload),
        onSuccess: () => {
            qc.invalidateQueries({ queryKey: queryKeys.assets.all })
            qc.invalidateQueries({ queryKey: queryKeys.folders.all })
        },
    })
}

/** PUT /assets/{id} */
export function useUpdateAsset() {
    const qc = useQueryClient()
    return useApiMutation({
        mutationKey: ['assets', 'update'],
        mutationFn: ({ id, ...payload }) =>
            api.put(`/assets/${id}`, {}, payload),
        onSuccess: (_d, { id }) => {
            qc.invalidateQueries({ queryKey: queryKeys.assets.detail(id) })
            qc.invalidateQueries({ queryKey: queryKeys.assets.lists() })
            qc.invalidateQueries({ queryKey: queryKeys.assets.all })
        },
    })
}

/** DELETE /assets/{id} */
export function useDeleteAsset() {
    const qc = useQueryClient()
    return useApiMutation({
        mutationKey: ['assets', 'delete'],
        mutationFn: (id) => api.delete(`/assets/${id}`),
        onSuccess: () => {
            qc.invalidateQueries({ queryKey: queryKeys.assets.all })
            qc.invalidateQueries({ queryKey: queryKeys.folders.all })
        },
    })
}

/** PUT /companies/{companyId}/assets/bulk — bulk update (e.g. move to folder). */
export function useBulkUpdateAssets() {
    const qc = useQueryClient()
    return useApiMutation({
        mutationKey: ['assets', 'bulkUpdate'],
        mutationFn: ({ companyId, ...payload }) =>
            api.put(`/companies/${companyId}/assets/bulk`, {}, payload),
        onSuccess: () => {
            qc.invalidateQueries({ queryKey: queryKeys.assets.all })
            qc.invalidateQueries({ queryKey: queryKeys.folders.all })
        },
    })
}
